import React, { useEffect, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import { pdfDocumentOptions } from "pdfWorker";

interface Props {
    content?: string;
    url?: string;
    contentType?: string;
    loading?: React.ReactNode;
    error?: React.ReactNode;
    className?: string;
    pageClassName?: string;
    maxWidth?: number;
    renderPageOverlay?: (pageNumber: number) => React.ReactNode;
}

const availableWindowWidth = () => Math.max(1, Math.min(800, window.innerWidth - 64));

const PdfDocumentViewer: React.FC<Props> = ({
    content,
    url,
    contentType = "application/pdf",
    loading,
    error,
    className = "overflow-auto",
    pageClassName = "mb-3 mx-auto shadow-sm position-relative",
    maxWidth = 800,
    renderPageOverlay,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [pageCount, setPageCount] = useState(0);
    const [pageWidth, setPageWidth] = useState(() => Math.min(maxWidth, availableWindowWidth()));

    useEffect(() => setPageCount(0), [content, url]);
    useEffect(() => {
        const resize = () => {
            const containerWidth = containerRef.current?.clientWidth || availableWindowWidth();
            setPageWidth(Math.max(1, Math.min(maxWidth, containerWidth)));
        };
        resize();
        window.addEventListener("resize", resize);
        const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(resize);
        if (containerRef.current) observer?.observe(containerRef.current);
        return () => {
            window.removeEventListener("resize", resize);
            observer?.disconnect();
        };
    }, [maxWidth]);

    return <div ref={containerRef} className={className}>
        <Document
            file={url || `data:${contentType};base64,${content ?? ""}`}
            options={pdfDocumentOptions}
            onLoadSuccess={({ numPages }) => setPageCount(numPages)}
            loading={loading}
            error={error}
        >
            {Array.from({ length: pageCount }, (_, index) => {
                const pageNumber = index + 1;
                return <div key={pageNumber} className={pageClassName} style={{ width: pageWidth }}>
                    <Page
                        pageNumber={pageNumber}
                        width={pageWidth}
                        renderAnnotationLayer={false}
                        renderTextLayer={false}
                    />
                    {renderPageOverlay?.(pageNumber)}
                </div>;
            })}
        </Document>
    </div>;
};

export default PdfDocumentViewer;
